import { Fragment } from 'react';

import { useDistribution } from '@/hooks/react-query/useDistributions';
import type { AssetSale } from '@/types/distribution';
import type { FundTransactionDetail } from '@/types/fund-transactions';

export function TransactionExtras({
  fundId,
  transaction,
}: {
  fundId: string;
  transaction: FundTransactionDetail;
}) {
  if (
    transaction.transaction_type === 'DISTRIBUTION' &&
    transaction.distribution
  ) {
    return (
      <DistributionExtras
        fundId={fundId}
        distributionId={transaction.distribution.distribution_id}
      />
    );
  }
  return null;
}

function DistributionExtras({
  fundId,
  distributionId,
}: {
  fundId: string;
  distributionId: string;
}) {
  const { data: distribution } = useDistribution(fundId, distributionId);

  if (!distribution) return null;

  const totalInvestors =
    distribution.total_investors ??
    distribution.investor_allocations?.length ??
    null;
  const investorsPaid = distribution.investors_paid ?? null;
  const portfolioCompany = distribution.portfolio_company_name ?? null;
  const assetSales = distribution.asset_sales ?? [];
  const assetLabels = buildAssetLabels(assetSales, portfolioCompany);

  if (totalInvestors == null && assetLabels.length === 0) return null;

  return (
    <DistributionSummaryProse
      totalInvestors={totalInvestors}
      investorsPaid={investorsPaid}
      assetLabels={assetLabels}
    />
  );
}

function DistributionSummaryProse({
  totalInvestors,
  investorsPaid,
  assetLabels,
}: {
  totalInvestors: number | null;
  investorsPaid: number | null;
  assetLabels: string[];
}) {
  const investorWord = (count: number) =>
    count === 1 ? 'investor' : 'investors';

  return (
    <div className="space-y-3 text-sm leading-6">
      <p>
        {totalInvestors != null ? (
          <>
            Allocated to <strong>{totalInvestors}</strong>{' '}
            {investorWord(totalInvestors)}
            {assetLabels.length > 0 && (
              <>
                {' '}
                from the sale of <AssetList labels={assetLabels} />
              </>
            )}
          </>
        ) : (
          <>
            Proceeds from the sale of <AssetList labels={assetLabels} />
          </>
        )}
        .
      </p>

      {investorsPaid != null &&
        totalInvestors != null &&
        totalInvestors > 0 && (
          <p className="text-muted-foreground">
            <strong>{investorsPaid}</strong> of {totalInvestors}{' '}
            {investorWord(totalInvestors)}{' '}
            {investorsPaid === 1 ? 'has' : 'have'} been paid.
          </p>
        )}
    </div>
  );
}

function AssetList({ labels }: { labels: string[] }) {
  return (
    <>
      {labels.map((label, idx) => (
        <Fragment key={label}>
          {idx > 0 && (idx === labels.length - 1 ? ' and ' : ', ')}
          <strong>{label}</strong>
        </Fragment>
      ))}
    </>
  );
}

function buildAssetLabels(
  assetSales: AssetSale[],
  portfolioCompany: string | null
): string[] {
  const fromSales = assetSales
    .map((sale) => sale.transaction_name ?? sale.portfolio_company_name)
    .filter((label): label is string => Boolean(label));

  if (fromSales.length > 0) return Array.from(new Set(fromSales));
  return portfolioCompany ? [portfolioCompany] : [];
}
